import { useRef, useState } from "react";
import { Link, useNavigate } from "../lib/router";
import { motion, useScroll, useTransform } from "framer-motion";
import { toast } from "sonner";
import { useLanguage } from "../i18n/LanguageContext";
import { useMeta } from "../hooks/useMeta";
import { api } from "../lib/api";
import { EASE, MaskedLines, Overline, Reveal } from "../components/motion";
import { ChipGroup, Field, PButton, TextArea } from "../components/forms";
import { EditionSelector, ProductStage } from "../components/editions";
import { DEFAULT_EDITION, EDITIONS, EQUIPMENT, REFORMERS } from "../data/products";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function Studios() {
  const { t, lang } = useLanguage();
  useMeta(t.seo.studios[0], t.seo.studios[1]);
  const navigate = useNavigate();

  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const heroY = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);

  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    company: "",
    email: "",
    phone: "",
    location: "",
    quantity: "",
    opening: "",
    message: "",
  });
  const [items, setItems] = useState([]);
  const [edition, setEdition] = useState(DEFAULT_EDITION);
  const [sending, setSending] = useState(false);

  const scrollToForm = () => {
    const el = document.getElementById("studio-form");
    if (!el) return;
    if (window.__lenis) window.__lenis.scrollTo(el, { offset: -40 });
    else el.scrollIntoView({ behavior: "smooth" });
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const toggleItem = (item) =>
    setItems((prev) => (prev.includes(item) ? prev.filter((i) => i !== item) : [...prev, item]));

  const itemOptions = [
    ...REFORMERS.map((p) => p.name),
    ...EQUIPMENT.map((p) => p.name),
    t.inquiry.studioPackage,
  ];

  const submit = async (e) => {
    e.preventDefault();
    if (!form.firstName.trim() || !form.lastName.trim()) {
      toast.error(`${t.common.required}: ${t.studios.firstName} / ${t.studios.lastName}`);
      return;
    }
    if (!EMAIL_RE.test(form.email)) {
      toast.error(t.common.invalidEmail);
      return;
    }
    setSending(true);
    try {
      await api.post("/inquiries", {
        type: "studio",
        first_name: form.firstName,
        last_name: form.lastName,
        company: form.company || null,
        email: form.email,
        phone: form.phone || null,
        location: form.location || null,
        items,
        finish: EDITIONS.find((e) => e.code === edition)?.label.de || null,
        quantity: form.quantity ? Number(form.quantity) : null,
        opening_date: form.opening || null,
        message: form.message || null,
        language: lang,
      });
      navigate("/danke");
    } catch {
      toast.error(t.common.formError);
      setSending(false);
    }
  };

  return (
    <div data-testid="studios-page">
      <section
        ref={heroRef}
        data-testid="studios-hero"
        className="relative flex h-[92svh] min-h-[560px] items-end overflow-hidden bg-[#1C1C1A]"
      >
        <motion.div style={{ y: heroY }} className="absolute inset-0">
          <img
            src="/images/studios-hero.jpg"
            alt="Luxuriöses Pilates Studio"
            className="h-[115%] w-full object-cover"
          />
        </motion.div>
        <div className="absolute inset-0 bg-[#1C1C1A]/45" />
        <div className="relative z-10 mx-auto w-full max-w-[1400px] px-6 pb-20 md:px-12 md:pb-28 lg:px-20">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2, duration: 1 }}
            data-testid="studios-overline"
            className="mb-6 font-mono text-[11px] uppercase tracking-[0.35em] text-[#DFCA9B]"
          >
            {t.studios.overline}
          </motion.p>
          <h1
            data-testid="studios-title"
            className="font-serif text-5xl font-light leading-[1.02] tracking-tight text-[#FAF8F5] md:text-7xl"
          >
            <MaskedLines lines={t.studios.title} mount delay={0.3} />
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.9, ease: EASE }}
            data-testid="studios-hero-text"
            className="mt-6 max-w-xl text-base font-light leading-relaxed text-[#FAF8F5]/80 md:text-lg"
          >
            {t.studios.heroText}
          </motion.p>
        </div>
      </section>

      <section data-testid="studios-photo" className="mx-auto max-w-[1400px] px-6 pt-20 md:px-12 md:pt-28 lg:px-20">
        <Reveal>
          <div className="group overflow-hidden">
            <img src="/images/products/studio-ivory.jpg" alt="Pilates Studio mit Reformern" className="aspect-[16/10] w-full object-cover transition-transform duration-[1600ms] ease-out group-hover:scale-[1.03]" />
          </div>
        </Reveal>
      </section>

      <section data-testid="studios-audiences" className="mx-auto max-w-[1400px] px-6 pt-24 md:px-12 md:pt-32 lg:px-20">
        <div className="grid gap-12 lg:grid-cols-12 lg:gap-16">
          <div className="lg:col-span-4">
            <Reveal y={14}>
              <Overline className="mb-6">{t.studios.audiencesOverline}</Overline>
            </Reveal>
            <Reveal delay={0.1}>
              <ul className="divide-y divide-[#1C1C1A]/10">
                {t.studios.audiences.map((a) => (
                  <li key={a} data-testid="studios-audience" className="py-4 font-serif text-2xl font-light text-[#1C1C1A] md:text-3xl">{a}</li>
                ))}
              </ul>
            </Reveal>
          </div>
          <div className="lg:col-span-7 lg:col-start-6">
            <Reveal y={14}>
              <Overline className="mb-6">{t.studios.focusOverline}</Overline>
            </Reveal>
            <div className="grid gap-10 md:grid-cols-3">
              {t.studios.focus.map((f, i) => (
                <Reveal key={f.t} delay={i * 0.12}>
                  <div data-testid={`studios-focus-${i + 1}`} className="border-t border-[#1C1C1A]/15 pt-6">
                    <h3 className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#1C1C1A]">{f.t}</h3>
                    <p className="mt-4 text-sm font-light leading-relaxed text-[#66625A]">{f.d}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section data-testid="studios-process" className="mx-auto max-w-[1400px] px-6 py-28 md:px-12 md:py-40 lg:px-20">
        <Reveal y={14}>
          <Overline className="mb-16">{t.studios.processOverline}</Overline>
        </Reveal>
        <div className="grid gap-12 md:grid-cols-2 lg:grid-cols-4">
          {t.studios.steps.map((s, i) => (
            <Reveal key={s.n} delay={i * 0.12}>
              <div data-testid={`process-step-${s.n}`} className="border-t border-[#1C1C1A]/15 pt-8">
                <p className="font-serif text-6xl font-light italic text-[#C5A059]">{s.n}</p>
                <h3 className="mt-6 font-mono text-[11px] uppercase tracking-[0.3em] text-[#1C1C1A]">
                  {s.t}
                </h3>
                <p className="mt-4 text-sm font-light leading-relaxed text-[#66625A]">{s.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section data-testid="studios-editions" className="border-y border-[#1C1C1A]/10">
        <div className="mx-auto grid max-w-[1400px] gap-12 px-6 py-24 md:px-12 md:py-32 lg:grid-cols-12 lg:gap-16 lg:px-20">
          <div className="lg:col-span-5">
            <Reveal y={14}>
              <Overline className="mb-6">{t.studios.editionsOverline}</Overline>
            </Reveal>
            <h2 data-testid="studios-editions-title" className="font-serif text-4xl font-light leading-[1.02] tracking-tight text-[#1C1C1A] md:text-6xl">
              <MaskedLines lines={t.studios.editionsTitle} />
            </h2>
            <Reveal delay={0.2}>
              <p className="mt-6 max-w-sm text-sm font-light leading-relaxed text-[#66625A]">{t.studios.editionsText}</p>
              <div className="mt-10">
                <EditionSelector value={edition} onChange={setEdition} size="lg" testPrefix="studios-edition" />
              </div>
              <div className="mt-12">
                <PButton onClick={scrollToForm} variant="solid" data-testid="studios-cta">{t.studios.cta}</PButton>
              </div>
            </Reveal>
          </div>
          <Reveal delay={0.15} className="lg:col-span-7">
            <ProductStage product={REFORMERS[0]} edition={edition} testId="studios-stage" className="aspect-[4/3] p-8 md:p-14" imgClassName="drop-shadow-[0_30px_40px_rgba(0,0,0,0.18)]" />
          </Reveal>
        </div>
      </section>

      <section id="studio-form" data-testid="studios-form-section" className="bg-[#F3EFEA]">
        <div className="mx-auto max-w-[1400px] px-6 py-28 md:px-12 md:py-36 lg:px-20">
          <Reveal y={14}>
            <Overline className="mb-6">{t.studios.formOverline}</Overline>
          </Reveal>
          <h2
            data-testid="studios-form-title"
            className="font-serif text-4xl font-light leading-[1.05] tracking-tight text-[#1C1C1A] md:text-6xl"
          >
            <MaskedLines lines={t.studios.formTitle} />
          </h2>

          <Reveal delay={0.25}>
            <form onSubmit={submit} className="mt-16 grid gap-x-12 gap-y-10 md:grid-cols-2" noValidate>
              <Field label={t.studios.firstName} id="studio-first-name" value={form.firstName} onChange={set("firstName")} required />
              <Field label={t.studios.lastName} id="studio-last-name" value={form.lastName} onChange={set("lastName")} required />
              <Field label={t.studios.company} id="studio-company" value={form.company} onChange={set("company")} />
              <Field label={t.studios.email} id="studio-email" type="email" value={form.email} onChange={set("email")} required />
              <Field label={t.studios.phone} id="studio-phone" type="tel" value={form.phone} onChange={set("phone")} />
              <Field label={t.studios.location} id="studio-location" value={form.location} onChange={set("location")} />

              <div className="md:col-span-2">
                <p className="mb-4 font-mono text-[10px] uppercase tracking-[0.25em] text-[#66625A]">
                  {t.studios.needed}
                </p>
                <ChipGroup options={itemOptions} selected={items} onToggle={toggleItem} testId="studio-items" />
              </div>

              <Field label={t.studios.quantity} id="studio-quantity" type="number" min="1" value={form.quantity} onChange={set("quantity")} />
              <Field label={t.studios.opening} id="studio-opening" value={form.opening} onChange={set("opening")} />

              <div className="md:col-span-2">
                <TextArea label={t.studios.message} id="studio-message" value={form.message} onChange={set("message")} />
              </div>

              <div className="md:col-span-2">
                <PButton type="submit" variant="solid" arrow={false} disabled={sending} data-testid="studio-form-submit">
                  {sending ? t.common.sending : t.studios.submit}
                </PButton>
                <p className="mt-4 max-w-2xl text-xs font-light leading-relaxed text-[#66625A]">Mit dem Absenden werden deine Angaben zur Bearbeitung deiner Anfrage verarbeitet. Weitere Informationen findest du in unserer <Link to="/datenschutz" className="underline underline-offset-2 transition-colors hover:text-[#C5A059]">Datenschutzerklärung</Link>.</p>
              </div>
            </form>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
